<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Expense',
  },
})

const { useApi } = await import('@/utils/api')
const router = useRouter()
const route = useRoute()

const expenseId = route.params.id

// ── State ────────────────────────────────────────────────────────────────────
const categories = ref([])
const loading = ref(true)
const saving = ref(false)
const errors = ref({})

const form = ref({
  expense_category_id: null,
  amount: '',
  description: '',
  expense_date: '',
  is_recurring: false,
})

const receiptFile = ref(null)
const receiptPreview = ref(null)
const existingReceiptUrl = ref(null)
const removeExistingReceipt = ref(false)

// ── Fetch data ────────────────────────────────────────────────────────────────
const fetchCategories = async () => {
  try {
    const response = await useApi('/api/v1/expense-categories', {
      query: { active_only: 1 },
    }).get()
    const data = response?.data ?? response
    categories.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error(e)
  }
}

const fetchExpense = async () => {
  loading.value = true
  try {
    const response = await useApi(`/api/v1/expenses/${expenseId}`).get()
    const data = response?.data ?? response

    form.value = {
      expense_category_id: data.expense_category_id,
      amount: String(Math.round(data.amount)),
      description: data.description,
      expense_date: data.expense_date,
      is_recurring: data.is_recurring,
    }

    existingReceiptUrl.value = data.receipt_image_url ?? null
  } catch (e) {
    console.error(e)
    router.push({ name: 'expense' })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await Promise.all([fetchCategories(), fetchExpense()])
})

// ── Receipt handling ─────────────────────────────────────────────────────────
const onReceiptChange = (file) => {
  if (!file) {
    receiptPreview.value = null
    receiptFile.value = null
    return
  }
  receiptFile.value = file
  const reader = new FileReader()
  reader.onload = (e) => { receiptPreview.value = e.target.result }
  reader.readAsDataURL(file)
}

const handleRemoveExisting = () => {
  existingReceiptUrl.value = null
  removeExistingReceipt.value = true
}

// ── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  saving.value = true
  errors.value = {}

  try {
    const fd = new FormData()
    fd.append('_method', 'PUT') // Laravel method spoofing untuk multipart
    fd.append('expense_category_id', form.value.expense_category_id)
    fd.append('amount', form.value.amount)
    fd.append('description', form.value.description)
    fd.append('expense_date', form.value.expense_date)
    fd.append('is_recurring', form.value.is_recurring ? '1' : '0')

    if (receiptFile.value) {
      fd.append('receipt_image', receiptFile.value)
    } else if (removeExistingReceipt.value) {
      fd.append('remove_receipt', '1')
    }

    const accessToken = useCookie('accessToken').value
    const tenantSlug = useCookie('current_tenant').value

    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }
    if (tenantSlug) headers['X-Tenant'] = tenantSlug

    const { $api } = await import('@/utils/api')
    await $api(`/api/v1/expenses/${expenseId}`, {
      method: 'POST', // POST dengan _method=PUT untuk FormData
      headers,
      body: fd,
    })

    router.push({ name: 'expense' })
  } catch (e) {
    if (e?.response?.status === 422) {
      errors.value = e?.data?.errors ?? {}
    } else {
      console.error(e)
    }
  } finally {
    saving.value = false
  }
}

// ── Format amount ────────────────────────────────────────────────────────────
const formatAmount = computed({
  get: () => form.value.amount ? Number(form.value.amount).toLocaleString('id-ID') : '',
  set: (val) => {
    form.value.amount = val.replace(/\D/g, '')
  },
})
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VBreadcrumbs
      :items="[
        { title: 'Pengeluaran', to: { name: 'expense' } },
        { title: 'Edit Pengeluaran', disabled: true },
      ]"
      class="px-0 mb-4"
    />

    <!-- Loading skeleton -->
    <VRow v-if="loading">
      <VCol cols="12" md="7" lg="8">
        <VCard>
          <VCardText class="pa-6">
            <VSkeletonLoader type="text, text, text, text, text" />
          </VCardText>
        </VCard>
      </VCol>
      <VCol cols="12" md="5" lg="4">
        <VCard>
          <VCardText class="pa-6">
            <VSkeletonLoader type="image, button" />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VRow v-else>
      <!-- Form -->
      <VCol cols="12" md="7" lg="8">
        <VCard>
          <VCardTitle class="pa-6 pb-0">Edit Pengeluaran</VCardTitle>
          <VCardText class="pa-6">
            <VRow>
              <!-- Kategori -->
              <VCol cols="12">
                <VSelect
                  v-model="form.expense_category_id"
                  :items="categories"
                  item-title="name"
                  item-value="id"
                  label="Kategori Pengeluaran *"
                  placeholder="Pilih kategori"
                  :error-messages="errors.expense_category_id"
                />
              </VCol>

              <!-- Deskripsi -->
              <VCol cols="12">
                <VTextField
                  v-model="form.description"
                  label="Deskripsi *"
                  placeholder="cth. Pembelian biji kopi arabica"
                  :error-messages="errors.description"
                />
              </VCol>

              <!-- Jumlah -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="formatAmount"
                  label="Jumlah (Rp) *"
                  placeholder="0"
                  prepend-inner-icon="ri-money-rupee-circle-line"
                  :error-messages="errors.amount"
                  inputmode="numeric"
                />
              </VCol>

              <!-- Tanggal -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="form.expense_date"
                  label="Tanggal Pengeluaran *"
                  type="date"
                  :error-messages="errors.expense_date"
                />
              </VCol>

              <!-- Berulang -->
              <VCol cols="12">
                <VSwitch
                  v-model="form.is_recurring"
                  label="Pengeluaran Berulang (bulanan)"
                  color="primary"
                  hide-details
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Struk & Actions -->
      <VCol cols="12" md="5" lg="4">
        <!-- Upload struk -->
        <VCard class="mb-4">
          <VCardTitle class="pa-6 pb-0">Foto Struk</VCardTitle>
          <VCardText class="pa-6">
            <!-- Existing receipt -->
            <template v-if="existingReceiptUrl && !receiptPreview">
              <VImg
                :src="existingReceiptUrl"
                max-height="200"
                rounded
                cover
                class="mb-3"
              />
              <div class="d-flex gap-2">
                <VBtn
                  variant="tonal"
                  color="error"
                  size="small"
                  prepend-icon="ri-delete-bin-line"
                  @click="handleRemoveExisting"
                >
                  Hapus
                </VBtn>
                <VBtn
                  :href="existingReceiptUrl"
                  target="_blank"
                  variant="tonal"
                  color="primary"
                  size="small"
                  prepend-icon="ri-external-link-line"
                >
                  Buka
                </VBtn>
              </div>
            </template>

            <!-- New receipt preview -->
            <template v-else-if="receiptPreview">
              <VImg
                :src="receiptPreview"
                max-height="200"
                rounded
                cover
                class="mb-3"
              />
              <VBtn
                variant="tonal"
                color="error"
                size="small"
                prepend-icon="ri-delete-bin-line"
                @click="onReceiptChange(null)"
              >
                Batal Ganti
              </VBtn>
            </template>

            <!-- Upload input -->
            <VFileInput
              v-else
              :label="existingReceiptUrl ? 'Ganti Struk' : 'Upload Struk (opsional)'"
              accept="image/*"
              prepend-icon="ri-camera-line"
              :error-messages="errors.receipt_image"
              hint="Maks. 2MB. Format: JPG, PNG, WEBP"
              persistent-hint
              @update:model-value="onReceiptChange"
            />
          </VCardText>
        </VCard>

        <!-- Actions -->
        <VCard>
          <VCardText class="pa-6">
            <VBtn
              color="primary"
              block
              size="large"
              :loading="saving"
              prepend-icon="ri-save-line"
              @click="submit"
            >
              Simpan Perubahan
            </VBtn>
            <VBtn
              variant="tonal"
              color="secondary"
              block
              class="mt-3"
              :disabled="saving"
              :to="{ name: 'expense' }"
            >
              Batal
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
